/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-syntax */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ValueSetterParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import lightStyles from './portalStylesLight.module.css';
import darkStyles from './portalStylesDark.module.css';
import { CodeTable } from '../NEW/NewDisposition';
import AddDialogue from './AddDialogue';
import Notes from './Notes';

type SubTab = 'Account' | 'Disposition' | 'Forced';

interface Props {
  dispositionCodes: CodeTable[];
  accountCodes: CodeTable[];
  forcedCodes: CodeTable[];
  orgId: string;
  token: string;
  lightMode: boolean;
  hasForcedAccountCodes: boolean;
  hasDispositionCodes: boolean;
  hasAccountCodes: boolean;
}

const MAX_LEN = 100;
const MAX_CODE_LEN = 50;
//const CODE_REGEX = /^[A-Za-z0-9\-_]+$/; We can add regexing in later if we want

export default function Codes({
  dispositionCodes,
  lightMode,
  accountCodes,
  forcedCodes,
  orgId,
  token,
  hasForcedAccountCodes,
  hasDispositionCodes,
  hasAccountCodes,
}: Props) {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>(hasDispositionCodes ? 'Disposition' : 'Account');
  const [rowData, setRowData] = useState<{ id: string; code: string; description: string }[]>([]);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<AgGridReact>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const styles = lightMode ? lightStyles : darkStyles;

  const subTabs = useMemo<SubTab[]>(() => {
    const tabs: SubTab[] = [];
    if (hasForcedAccountCodes) {
      tabs.push('Forced');
    }
    if (hasAccountCodes) {
      tabs.push('Account');
    }
    if (hasDispositionCodes) {
      tabs.push('Disposition');
    }
    return tabs;
  }, [hasForcedAccountCodes]);

  // Initialize row data
  useEffect(() => {
    let dataset;
    switch (activeSubTab) {
      case 'Disposition':
        dataset = dispositionCodes[0].items;
        break;
      case 'Account':
        dataset = accountCodes[0].items;
        break;
      default:
        dataset = forcedCodes[0].items;
        break;
    }
    const formattedData = dataset.map((codeItem) => ({
      id: `${codeItem.code}#${codeItem.id}`,
      code: codeItem.id.split(':')[0],
      description: codeItem.id.split(':')[1],
    }));
    setRowData(formattedData);
  }, [activeSubTab, dispositionCodes, accountCodes, forcedCodes]);

  const getSelectedRows = useCallback(() => {
    if (gridRef.current) {
      return gridRef.current.api.getSelectedRows();
    }
    return [];
  }, [gridRef]);

  const handleAddClick = useCallback(() => {
    setShowAddDropdown((prev) => !prev);
  }, []);

  const handleAdd = useCallback(
    async (newCodes: { id: string; description: string }[]) => {
      try {
        setError(null);
        const type = activeSubTab.toLowerCase();
        const group = `default-${type}`;
        const body = newCodes.map((code) => ({ code: code.id, description: code.description, group }));
        const res = await fetch(`${process.env.REACT_APP_API_URL}/code`, {
          method: 'POST',
          body: JSON.stringify(body),
          headers: {
            authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error(`Failed to add code: ${res.statusText}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add code');
        console.error('Error adding code:', err);
      }
    },
    [activeSubTab, token],
  );

  const handleDelete = useCallback(async () => {
    try {
      setError(null);
      const selectedRows = getSelectedRows();
      if (selectedRows.length === 0) {
        setError('No rows selected for deletion');
        return;
      }
      const type = activeSubTab.toLowerCase();

      const group = `default-${type}`;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/code`, {
        method: 'DELETE',
        body: JSON.stringify(
          selectedRows.map((row) => ({
            code: row.code,
            description: row.description,
            group,
          })),
        ),
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to delete code: ${res.statusText}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete code');
      console.error('Error deleting code:', err);
    }
  }, [getSelectedRows, activeSubTab, token]);

  const onCellValueChanged = useCallback(
    async (event: any) => {
      try {
        setError(null);
        const { oldValue, newValue } = event;
        const group = `default-${activeSubTab.toLowerCase()}`;
        const body = {
          code: event.data.code,
          description: newValue,
          group,
          oldDescription: oldValue,
        };
        const res = await fetch(`${process.env.REACT_APP_API_URL}/code`, {
          method: 'PUT',
          body: JSON.stringify(body),
          headers: {
            authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error(`Failed to update code: ${res.statusText}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update code');
        console.error('Error updating code:', err);
      }
    },
    [activeSubTab, token],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: '',
        field: 'selected',
        headerCheckboxSelection: true,
        checkboxSelection: true,
        suppressMenu: true,
        width: 50,
        suppressSizeToFit: true,
      },
      {
        headerName: 'Code',
        field: 'code',
        editable: false,
        width: 150,
      },
      {
        headerName: 'Description',
        field: 'description',
        editable: true,
        flex: 1,
        valueSetter: (params: ValueSetterParams<{ id: string; code: string; description: string }>) => {
          const v = (params.newValue ?? '').toString();
          if (v.length > MAX_LEN || v.length < 1) {
            return false;
          }
          params.data.description = v;
          return true;
        },
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 50,
    }),
    [],
  );

  return (
    <div className={styles.gridContainer}>
      {error && (
        <div
          className={styles.errorMessage}
          style={{
            color: 'red',
            padding: '8px',
            margin: '8px 0',
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            borderRadius: '4px',
            border: '1px solid red',
          }}
        >
          {error}
        </div>
      )}
      <div className={styles.navbar}>
        <div className={styles.tabButtons}>
          {subTabs.map((subTab) => (
            <button
              key={subTab}
              type="button"
              onClick={() => setActiveSubTab(subTab)}
              className={`${styles.subTabButton} ${activeSubTab === subTab ? styles.subTabButtonActive : ''}`}
            >
              {subTab}
            </button>
          ))}
        </div>
        <div className={styles.actionButtonsContainer}>
          <div className={styles.actionButtonsWrapper}>
            <button ref={addButtonRef} type="button" onClick={handleAddClick} className={styles.actionButton}>
              Add
            </button>
            {showAddDropdown && (
              <AddDialogue
                onAdd={handleAdd}
                onClose={() => setShowAddDropdown(false)}
                format='"&lt;code&gt; &lt;description&gt;"'
                lightMode={lightMode}
                example='"1000 Emergency"'
                params={2}
                validate={(codes) => {
                  for (const code of codes) {
                    if (code.id.length < 1) {
                      return `Code cannot be empty.`;
                    }
                    if (code.id.length > MAX_CODE_LEN) {
                      return `Code "${code.id}" is too long (must be ${MAX_CODE_LEN} characters or less).`;
                    } /*
                    if (!CODE_REGEX.test(code.id)) {
                      return `Code "${code.id}" contains invalid characters. Only letters, numbers, hyphens, and underscores are allowed.`;
                    }*/
                    if (code.description.length < 1) {
                      return `Code "${code.id}" is missing description`;
                    }
                    if (code.description.length > MAX_LEN) {
                      return `Description for "${code.id} - ${code.description}" is too long (must be less than ${MAX_LEN} character)`;
                    }
                  }
                  return null;
                }}
              />
            )}
            <button type="button" onClick={handleDelete} className={styles.actionButton}>
              Delete
            </button>
          </div>
        </div>
      </div>

      <div
        className={`ag-theme-alpine${!lightMode ? '-dark' : ''} ${styles.agThemeAlpineOverrides} ${styles.gridWrapper}`}
        style={{ flex: 3 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          animateRows
          suppressCellFocus
          rowSelection="multiple"
          suppressRowClickSelection
          suppressDragLeaveHidesColumns
          headerHeight={40}
          rowHeight={40}
        />
      </div>
      <Notes
        lightMode={lightMode}
        notes={`
            This page controls what dispostion codes exist within your system.
            These codes will be available upon page refresh for your agents inside
            of the code dropdown boxes in the code tab.
            
            Codes must be between 1 and ${MAX_CODE_LEN} characters and can only contain letters, numbers, hyphens, and underscores.
            Descriptions must be between 1 and ${MAX_LEN} characters.
          `}
        // documents={[
        //   { name: 'Supporting Document', uri: 'https://google.ca' },
        //   { name: 'Instruction Manual', uri: 'https://yahoo.ca' },
        //   { name: 'Demo', uri: 'https://bing.com' },
        // ]}
      />
    </div>
  );
}
